import React, { useEffect, useState } from 'react';
import { Sun, Cloud, CloudRain, CloudLightning, CloudSnow, Wind, MapPin, Droplets } from 'lucide-react';

interface WeatherData {
  temperature: number;
  weathercode: number;
  windspeed: number;
}

export const WeatherWidget: React.FC = () => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Coordinates for São Paulo: Lat -23.55, Lon -46.63
        const response = await fetch(
          'https://api.open-meteo.com/v1/forecast?latitude=-23.55&longitude=-46.63&current_weather=true&timezone=America%2FSao_Paulo'
        );
        
        if (!response.ok) throw new Error('Weather fetch failed');
        
        const data = await response.json();
        setWeather(data.current_weather);
      } catch (err) {
        console.error("Weather widget error:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, []);

  // Helper to determine theme based on weather code
  const getWeatherTheme = (code: number) => {
    // Clear Sky (0)
    if (code === 0) {
      return {
        gradient: 'from-green-500/20 to-emerald-500/10',
        border: 'border-green-500',
        iconColor: 'text-green-400',
        textColor: 'text-green-100',
        label: 'Céu Limpo',
        Icon: Sun
      };
    }
    // Cloudy (1-3, 45-48)
    if (code >= 1 && code <= 48) {
      return {
        gradient: 'from-slate-600/30 to-slate-500/10',
        border: 'border-slate-400',
        iconColor: 'text-slate-300',
        textColor: 'text-slate-200',
        label: 'Nublado',
        Icon: Cloud
      };
    }
    // Rain (51-67, 80-82)
    if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) {
      return {
        gradient: 'from-blue-600/30 to-cyan-500/10',
        border: 'border-blue-500',
        iconColor: 'text-blue-400',
        textColor: 'text-blue-100',
        label: 'Chuvoso',
        Icon: CloudRain
      };
    }
    // Snow (71-77) - Rare in SP, but handled
    if (code >= 71 && code <= 77) {
      return {
        gradient: 'from-cyan-100/20 to-blue-100/5',
        border: 'border-white',
        iconColor: 'text-white',
        textColor: 'text-white',
        label: 'Neve',
        Icon: CloudSnow
      };
    }
    // Storm (95-99)
    if (code >= 95 && code <= 99) {
      return {
        gradient: 'from-purple-600/30 to-indigo-500/10',
        border: 'border-purple-500',
        iconColor: 'text-yellow-400', // Lightning often yellow
        textColor: 'text-purple-100',
        label: 'Tempestade',
        Icon: CloudLightning
      };
    }
    
    // Default fallback
    return {
      gradient: 'from-slate-700/50 to-slate-800',
      border: 'border-slate-600',
      iconColor: 'text-slate-400',
      textColor: 'text-slate-300',
      label: 'Normal',
      Icon: Sun
    };
  };

  if (loading) {
    return (
      <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 shadow-lg flex items-center justify-between animate-pulse h-[88px]">
        <div className="flex flex-col gap-2">
           <div className="h-4 w-24 bg-slate-700 rounded"></div>
           <div className="h-6 w-16 bg-slate-700 rounded"></div>
        </div>
        <div className="h-10 w-10 bg-slate-700 rounded-full"></div>
      </div>
    );
  }

  if (error || !weather) {
    return null;
  }

  const theme = getWeatherTheme(weather.weathercode);
  const CurrentIcon = theme.Icon;

  return (
    <div className={`relative overflow-hidden rounded-xl border border-slate-700 shadow-lg bg-gradient-to-br ${theme.gradient} border-l-4 ${theme.border} transition-all duration-500`}>
      <div className="p-4 flex items-center justify-between">
        
        {/* Left Side: Info */}
        <div className="z-10">
          <div className="flex items-center gap-1.5 text-slate-400 text-[10px] font-bold uppercase tracking-wider mb-1">
              <MapPin className="w-3 h-3" />
              <span>São Paulo</span>
          </div>
          
          <div className="flex items-baseline gap-3">
              <h3 className="text-4xl font-bold text-white tracking-tighter">
                {Math.round(weather.temperature)}°
              </h3>
              <div className="flex flex-col">
                  <span className={`text-sm font-semibold ${theme.textColor}`}>
                      {theme.label}
                  </span>
                  <div className="flex items-center gap-1 text-xs text-slate-400">
                      <Wind className="w-3 h-3" />
                      {weather.windspeed} km/h
                  </div>
              </div>
          </div>
        </div>

        {/* Right Side: Icon */}
        <div className={`p-3 rounded-2xl bg-slate-900/30 backdrop-blur-sm border border-white/10 ${theme.iconColor}`}>
           <CurrentIcon className="w-8 h-8" />
        </div>
      </div>
    </div>
  );
};